<template>
	<transition @enter="onEnter" @leave="onLeave">
		<section
			class="lightbox"
			v-if="show"
			ref="lightbox"
			@click.self="handleOutsideClick"
		>
			<div class="inner" ref="panel" @click.stop>
				<div class="form" :style="formMinHeightStyle">
					<transition name="form-swap" mode="out-in">
						<div
							class="form-inner"
							v-if="!submitSuccess"
							key="form"
							ref="formInner"
						>
							<h1 class="form_field form__input--full">{{ popupHeader }}</h1>
							<div class="form_field" :class="{ is_filed: formData.name }">
								<input
									type="text"
									class="form__input"
									v-model="formData.name"
								/>
								<label>{{ messages[lang].form.name }}</label>
							</div>
							<div class="form_field" :class="{ is_filed: formData.lastName }">
								<input
									type="text"
									class="form__input"
									v-model="formData.lastName"
								/>
								<label>{{ messages[lang].form.lastName }}</label>
							</div>
							<div
								class="form_field form_field--full"
								:class="{ is_filed: formData.email }"
							>
								<input
									type="email"
									class="form__input form__input--full"
									v-model="formData.email"
								/>
								<label>{{ messages[lang].form.email }}</label>
							</div>
							<div
								class="form_field form_field--full"
								:class="{ is_filed: formData.message }"
							>
								<textarea
									class="form__textarea form__input--full"
									v-model="formData.message"
								></textarea>
								<label>{{ messages[lang].form.message }}</label>
							</div>
							<div class="form_field form_field--full response-grid">
								<div class="span-4">
									<div
										v-if="errorMessage"
										class="form_feedback form_feedback--error"
										role="alert"
										v-html="errorMessage"
									></div>
								</div>
								<div class="span-1 text-right">
									<button
										type="button"
										class="sent"
										:disabled="isSubmitDisabled"
										@click="submitForm"
										:aria-busy="isSubmitting.toString()"
									>
										<span v-if="!isSubmitting">{{
											messages[lang].form.sent
										}}</span>
										<span v-else>{{ messages[lang].form.sending }}</span>
									</button>
								</div>
							</div>
						</div>
						<div class="form-confirm" v-else key="confirm">
							<svg
								width="100"
								height="101"
								viewBox="0 0 100 101"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M12.6097 87.3406C21.0155 95.7244 32.6439 100.246 47.495 100.901C49.1746 101.033 50.8254 101.033 52.5069 100.901C58.7847 101.033 60.8046 99.5279 63.2958 99.1424L95.9893 71.1881C97.3297 66.2319 100 57.7577 100 51.7656C100 51.2282 99.9962 48.2611 99.9846 47.7323C99.696 33.299 95.4633 21.9717 87.2864 13.7573C78.8095 5.24008 67.2176 0.653726 52.5069 0H47.495C32.6439 0.653726 21.0155 5.17505 12.6097 13.5605C4.38472 21.7629 0.184704 33.352 0.00577545 48.3244C0.00192752 48.6581 0 51.4285 0 51.7656C0 67.0957 4.20386 78.9534 12.6097 87.3406Z"
									fill="#FF3D37"
								/>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M63.2958 99.1424C63.2958 99.1424 62.0995 91.5571 74.9195 80.0753C87.7394 68.5935 95.9893 71.188 95.9893 71.188C95.9893 71.188 85.0814 81.3253 79.5915 86.0194C74.1016 90.7135 63.2958 99.1424 63.2958 99.1424Z"
									fill="#BE3930"
								/>
								<rect
									x="33"
									y="36"
									width="35"
									height="26"
									rx="4"
									stroke="white"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M50.503 52L33.0015 38.9088L33 36H68V38.9088L50.503 52Z"
									fill="white"
									stroke="white"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M47.0303 42.7087L50.3365 46L55.2565 39.8479"
									stroke="#BE3930"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<h1 v-html="successMessage" role="status"></h1>
							<p class="form_feedback form_feedback--success info">
								{{ messages[lang].form.info }}
							</p>
							<div class="form-confirm__progress">
								<span
									class="progress"
									:key="`progress-${progressKey}`"
									:style="progressBarStyle"
								></span>
								<span class="progress-label"> {{ closeDelaySeconds }}s </span>
							</div>
						</div>
					</transition>
					<button type="button" ref="close" class="close" @click="close">
						<IconClose />
					</button>
				</div>
			</div>
			<div class="bg" ref="backdrop" @click="handleOutsideClick"></div>
		</section>
	</transition>
</template>

<script>
const messages = {
	en: {
		form: {
			sent: "Send",
			sending: "Sending...",
			name: "Name",
			lastName: "Last Name",
			email: "Email",
			message: "Message",
			info: "This popup will close in 5 seconds.",
		},
	},
	de: {
		form: {
			sent: "Senden",
			sending: "Senden...",
			name: "Name",
			lastName: "Nachname",
			email: "Email",
			message: "Nachricht",
			info: "Dieses Pop-up schließt sich in 5 Sekunden.",
		},
	},
	fr: {
		form: {
			sent: "Envoyer",
			sending: "Envoi...",
			name: "Nom",
			lastName: "Nom de famille",
			email: "Email",
			message: "Message",
			info: "Cette fenêtre contextuelle se fermera dans 5 secondes.",
		},
	},
	it: {
		form: {
			sent: "Invia",
			sending: "Invio...",
			name: "Nome",
			lastName: "Cognome",
			email: "Email",
			message: "Messaggio",
			info: "Questo popup si chiuderà in 5 secondi.",
		},
	},
};
import { mapState } from "vuex";
import gsap from "gsap";
import Service from "@/api/Service";
import IconClose from "@/components/icons/IconClose";

export default {
	name: "AdvicesList",
	props: {
		title: String,
		id: String,
		popupHeader: String,
		confirmMessage: String,
		show: Boolean,
	},
	data() {
		return {
			formData: {
				name: "",
				lastName: "",
				email: "",
				message: "",
			},
			messages,
			previousBodyOverflow: null,
			isSubmitting: false,
			submitSuccess: false,
			errorMessage: "",
			defaultErrorMessage:
				"Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
			formMinHeight: 0,
			autoCloseDelay: 5000,
			autoCloseTimer: null,
			progressKey: 0,
		};
	},
	watch: {
		show: {
			immediate: true,
			handler(isVisible) {
				if (typeof document === "undefined") return;
				if (isVisible) {
					if (this.previousBodyOverflow === null) {
						this.previousBodyOverflow = document.body.style.overflow || "";
					}
					document.body.style.overflow = "hidden";
					this.resetFeedback();
					this.captureFormHeight();
				} else {
					if (this.previousBodyOverflow !== null) {
						document.body.style.overflow = this.previousBodyOverflow;
						this.previousBodyOverflow = null;
					}
					this.resetForm();
					this.resetFeedback();
				}
			},
		},
	},
	mounted() {
		this.captureFormHeight();
	},
	beforeUnmount() {
		if (typeof document === "undefined") return;
		document.body.style.overflow =
			this.previousBodyOverflow !== null ? this.previousBodyOverflow : "";
		this.previousBodyOverflow = null;
		this.clearAutoCloseTimer();
	},
	components: {
		IconClose,
	},
	computed: {
		...mapState({
			lang: (state) => state.site.currentLanguage,
		}),
		isFormFilled() {
			return (
				this.formData.name &&
				this.formData.lastName &&
				this.formData.email &&
				this.formData.message
			);
		},
		isSubmitDisabled() {
			return !this.isFormFilled || this.isSubmitting;
		},
		successMessage() {
			return this.confirmMessage || "Vielen Dank! Wir melden uns in Kuerze.";
		},
		closeDelaySeconds() {
			return Math.round(this.autoCloseDelay / 1000);
		},
		progressBarStyle() {
			return {
				animationDuration: `${this.closeDelaySeconds}s`,
			};
		},
		formMinHeightStyle() {
			return this.formMinHeight ? { minHeight: `${this.formMinHeight}px` } : {};
		},
	},
	methods: {
		close() {
			this.clearAutoCloseTimer();
			this.$emit("close");
		},
		handleOutsideClick() {
			this.close();
		},
		captureFormHeight() {
			this.$nextTick(() => {
				if (this.submitSuccess) return;
				const formInner = this.$refs.formInner;
				if (formInner) {
					this.formMinHeight = formInner.offsetHeight;
				}
			});
		},
		startAutoCloseTimer() {
			this.clearAutoCloseTimer();
			this.progressKey += 1;
			this.autoCloseTimer = setTimeout(() => {
				this.autoCloseTimer = null;
				this.close();
			}, this.autoCloseDelay);
		},
		clearAutoCloseTimer() {
			if (this.autoCloseTimer) {
				clearTimeout(this.autoCloseTimer);
				this.autoCloseTimer = null;
			}
		},
		async submitForm() {
			if (this.isSubmitDisabled) return;

			this.errorMessage = "";
			this.submitSuccess = false;
			this.isSubmitting = true;

			try {
				const nonce =
					typeof window !== "undefined" && window.ProcoreRest
						? window.ProcoreRest.nonce
						: "";

				await Service.post("/contact", this.buildPayload(), {
					headers: {
						"Content-Type": "application/json",
						"X-WP-Nonce": nonce,
					},
				});
				this.submitSuccess = true;
				this.resetForm();
				this.startAutoCloseTimer();
			} catch (error) {
				this.errorMessage = this.extractErrorMessage(error);
			} finally {
				this.isSubmitting = false;
			}
		},
		buildPayload() {
			return {
				name: this.formData.name,
				last_name: this.formData.lastName,
				email: this.formData.email,
				message: this.formData.message,
				source_id: this.id,
				page_url: window.location?.href || "",
			};
		},
		extractErrorMessage(error) {
			if (error && error.response && error.response.data) {
				const data = error.response.data;
				if (typeof data === "string") {
					return data;
				}
				if (data.message) {
					return data.message;
				}
				if (data.error) {
					return data.error;
				}
			}
			return this.defaultErrorMessage;
		},
		resetForm() {
			this.formData.name = "";
			this.formData.lastName = "";
			this.formData.email = "";
			this.formData.message = "";
		},
		resetFeedback() {
			this.submitSuccess = false;
			this.errorMessage = "";
			this.clearAutoCloseTimer();
			this.progressKey += 1;
		},
		onEnter(_, done) {
			const complete = typeof done === "function" ? done : () => {};
			if (typeof window === "undefined") {
				complete();
				return;
			}
			this.animateLightbox({ opening: true, done: complete });
		},
		onLeave(_, done) {
			const complete = typeof done === "function" ? done : () => {};
			if (typeof window === "undefined") {
				complete();
				return;
			}
			this.animateLightbox({ opening: false, done: complete });
		},
		animateLightbox({ opening, done }) {
			const complete = typeof done === "function" ? done : () => {};
			const backdrop = this.$refs.backdrop;
			const panel = this.$refs.panel;
			const lightbox = this.$refs.lightbox;
			const close = this.$refs.close;

			if (!backdrop || !panel || !lightbox) {
				complete();
				return;
			}

			const timeline = gsap.timeline({
				onComplete: complete,
			});

			if (opening) {
				timeline
					.fromTo(
						backdrop,
						{ opacity: 0 },
						{
							opacity: 0.9,
							duration: 0.3,
							ease: "power1.out",
							onComplete: () => {
								gsap.set(backdrop, { clearProps: "opacity" });
							},
						}
					)
					.fromTo(
						lightbox,
						{ backdropFilter: "blur(0px)" },
						{ backdropFilter: "blur(9px)", duration: 0.4, ease: "power1.out" },
						"<"
					)
					.fromTo(
						panel,
						{ y: 40, opacity: 0 },
						{
							y: 0,
							opacity: 1,
							duration: 0.5,
							ease: "power2.out",
							onComplete: () => {
								gsap.set(panel, { clearProps: "transform" });
							},
						},
						"<"
					)
					.fromTo(
						close,
						{ opacity: 0 },
						{
							opacity: 1,
							duration: 0.3,
							ease: "power2.out",
							onComplete: () => {
								gsap.set(close, { clearProps: "opacity" });
							},
						}
					);
			} else {
				timeline
					.fromTo(
						close,
						{ opacity: 1 },
						{
							opacity: 0,
							duration: 0.3,
							ease: "power2.out",
						}
					)
					.to(panel, {
						y: 40,
						opacity: 0,
						duration: 0.3,
						ease: "power2.in",
					})
					.to(
						lightbox,
						{ backdropFilter: "blur(0px)", duration: 0.3, ease: "power1.in" },
						"<"
					)
					.to(backdrop, { opacity: 0, duration: 0.3, ease: "power1.in" }, "<");
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.lightbox {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	justify-content: center;
	align-items: center;
	box-sizing: border-box;
	z-index: 1000;
	backdrop-filter: blur(9px);
	padding: 20px;

	@include mq($to: m) {
		padding: 0;
	}

	.response-grid {
		display: grid;
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: 20px;

		.span-1 {
			grid-column: span 1;

			@include mq($to: m) {
				grid-column: span 5;
			}
		}

		.span-4 {
			grid-column: span 4;

			@include mq($to: m) {
				grid-column: span 5;
			}
		}
	}

	.text-right {
		text-align: right;
	}

	.close {
		position: absolute;
		top: 24px;
		right: 24px;
		border: none;
		outline: none;
		background: transparent;
		cursor: pointer;

		@include mq($to: m) {
			position: fixed;
		}

		&:hover {
			color: $colorRed1;
		}
	}

	h1 {
		@include fontH1;
		margin-bottom: 16px;

		@include mq($to: m) {
			margin-top: 8px;
		}
	}

	.form-confirm {
		min-height: inherit;
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;

		.info {
			@include fontCopy;
			text-align: center;
			margin-top: 12px;
		}

		.form-confirm__progress {
			width: 100%;
			max-width: 320px;
			height: 4px;
			margin: 20px auto 0;
			border-radius: 999px;
			background: rgba(0, 0, 0, 0.15);
			overflow: hidden;

			.progress {
				display: block;
				width: 100%;
				height: 100%;
				background: $colorRed2;
				transform-origin: left;
				animation-name: form-confirm-progress;
				animation-timing-function: linear;
				animation-fill-mode: forwards;
				border-radius: 999px;
			}

			.progress-label {
				@include fontCopy;
				display: block;
				margin-top: 8px;
				font-size: 12px;
				text-align: center;
				color: rgba(0, 0, 0, 0.6);
			}
		}

		svg {
			position: absolute;
			left: 50%;
			top: 0;
			transform: translate(-50%, -50%);
		}

		h1 {
			text-align: center;
			max-width: 400px;
			margin: 0 auto;
		}
	}

	.form-inner {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 20px;

		.form_field {
			width: 100%;
			position: relative;

			@include mq($to: m) {
				grid-column: span 2;
			}
		}

		.form_field--full {
			grid-column: span 2;
		}

		label {
			@include fontCopy;
			position: absolute;
			top: 1px;
			left: 1px;
			height: 38px;
			display: block;
			border-radius: 4px;
			outline: none;
			background: $colorBeige3;
			padding: 7px 10px;
			line-height: 24px;
			pointer-events: none;
			transition: transform 0.3s;
			transform-origin: left top;
		}

		.form_field.is_filed label,
		textarea:focus + label,
		input:focus + label {
			transform: translate(5px, -12px) scale(0.625);
		}

		textarea:focus,
		input:focus {
			outline: 1px solid #000;
		}

		.form_field.is_filed textarea,
		.form_field.is_filed input {
			border: 1px solid rgba(48, 46, 46, 1);
		}

		input,
		textarea {
			@include fontCopy;
			line-height: 40px;
			width: 100%;
			border-radius: 4px;
			border: 1px solid rgba(48, 46, 46, 0.4);
			height: 40px;
			outline: none;
			background: transparent;
			padding: 8px 10px;
			resize: none;
		}

		textarea {
			min-height: 120px;
		}

		.form__input--full {
			grid-column: span 2;
		}

		button.sent {
			margin-left: auto;
			justify-self: flex-start;
		}

		.form_feedback {
			@include fontCopy;
			margin: 0;
			line-height: 1.4;

			&--error {
				color: $colorRed1;
			}

			&--success {
				color: $colorGraphite;
			}
		}
	}

	button.sent {
		display: inline-block;
		text-align: left;
		background: $colorRed1;
		border-radius: 33% 40% 35% 45% / 60% 60% 60% 60%;
		padding: 5px 15px;
		margin-left: -15px;
		outline: none;
		border: none;
		font-weight: 500;
		color: #000;
		font-family: Rubik;
		font-size: 18px;
		font-style: normal;
		font-weight: 500;
		line-height: 28px; /* 155.556% */
		text-decoration-line: underline;
		text-decoration-style: solid;
		text-decoration-skip-ink: none;
		text-decoration-thickness: auto;
		text-underline-offset: auto;
		text-underline-position: from-font;
		cursor: pointer;

		&:disabled {
			color: rgba(0, 0, 0, 0.2);
			cursor: not-allowed;
		}
	}

	.inner {
		position: relative;
		z-index: 11;
		max-width: 730px;
		width: 100%;
		background: $colorBeige3;
		padding: 20px 30px;

		@include mq($to: m) {
			overflow-y: scroll;
		}
	}

	.bg {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 10;
		opacity: 0.9;
		background: $colorBeige1;

		@include mq($to: m) {
			background: $colorBeige3;
			opacity: 1;
		}
	}

	.form-swap-enter-active,
	.form-swap-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.form-swap-enter-from,
	.form-swap-leave-to {
		opacity: 0;
		transform: translateY(20px);
	}
}

@keyframes form-confirm-progress {
	from {
		transform: scaleX(0);
	}
	to {
		transform: scaleX(1);
	}
}
</style>
